import { css } from '@emotion/react';
import { Fragment, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Collapse,
  Tooltip,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import INodeAgent from 'types/INodeAgent';
import deleteNodeAgent from 'store/deleteNodeAgent';
import deleteWorker from 'store/deleteWorker';
import sendNodeCommand from 'store/sendNodeCommand';
import CleanupWorkerDialog from './CleanupWorkerDialog';
import RetireWorkerDialog from './RetireWorkerDialog';
import StopWorkerDialog from './StopWorkerDialog';
import UpgradeWorkerDialog from './UpgradeWorkerDialog';

interface NodeTableProps {
  nodes: INodeAgent[];
  onStartWorker: (nodeId: string) => void;
  provisioningNodeId: string | null;
  onMutate: () => void;
}

function formatLastSeen(lastSeen: string): string {
  if (!lastSeen) return 'Never';
  const date = new Date(lastSeen);
  if (isNaN(date.getTime())) return 'Invalid';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const time = date.toLocaleTimeString(undefined, { hour12: false });
  return `${y}-${m}-${d} ${time}`;
}

export default function NodeTable({ nodes, onStartWorker, provisioningNodeId, onMutate }: NodeTableProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [cleanupTarget, setCleanupTarget] = useState<{
    nodeAgentId: string;
    instanceId: string;
    workerName: string;
  } | null>(null);
  const [retireTarget, setRetireTarget] = useState<{
    workerId: string;
    workerName: string;
    packageName?: string;
    packageVersion?: string;
    trackingKey: string;
  } | null>(null);
  const [retiringWorkers, setRetiringWorkers] = useState<Set<string>>(new Set());
  const retiringTimestamps = useRef<Map<string, number>>(new Map());
  const [cleaningUpWorkers, setCleaningUpWorkers] = useState<Set<string>>(new Set());
  const [stoppingWorkers, setStoppingWorkers] = useState<Set<string>>(new Set());
  const [stopTarget, setStopTarget] = useState<{
    nodeAgentId: string;
    instanceId: string;
    workerName: string;
  } | null>(null);
  const [removingWorkers, setRemovingWorkers] = useState<Set<string>>(new Set());
  const [deletingWorkers, setDeletingWorkers] = useState<Set<string>>(new Set());
  const [removingNodes, setRemovingNodes] = useState<Set<string>>(new Set());
  const [upgradingWorkers, setUpgradingWorkers] = useState<Map<string, string | undefined>>(new Map());
  const [upgradeTarget, setUpgradeTarget] = useState<{
    nodeAgentId: string;
    instanceId: string;
    workerName: string;
    currentPackageName: string;
    currentVersion?: string;
    workdirName?: string;
  } | null>(null);

  // Auto-expand the node that is being provisioned
  useEffect(() => {
    if (provisioningNodeId) {
      setExpandedNodes((prev) => {
        if (prev.has(provisioningNodeId)) return prev;
        return new Set(prev).add(provisioningNodeId);
      });
    }
  }, [provisioningNodeId]);

  // Clear upgradingWorkers entries once the upgrade completes.
  // We track the installed_version at the time the upgrade was initiated;
  // once the version changes (or the status becomes UPGRADE_FAILED), we
  // know the upgrade finished and can clear the local flag.
  useEffect(() => {
    if (upgradingWorkers.size === 0) return;
    const confirmed: string[] = [];
    for (const node of nodes) {
      for (const ws of node.worker_summaries || []) {
        if (!ws.instance_id) continue;
        const key = `${node.id}:${ws.instance_id}`;
        if (!upgradingWorkers.has(key)) continue;
        const oldVersion = upgradingWorkers.get(key);
        const currentVersion = ws.installed_version || ws.package_version;
        if (ws.status === 'UPGRADE_FAILED' || (oldVersion && currentVersion && currentVersion !== oldVersion)) {
          confirmed.push(key);
        }
      }
    }
    if (confirmed.length > 0) {
      setUpgradingWorkers((prev) => {
        const next = new Map(prev);
        for (const key of confirmed) next.delete(key);
        return next;
      });
    }
  }, [nodes, upgradingWorkers]);

  // Fallback: clear all upgrading entries after 30s.
  // Handles the case where upgrade completes between data refreshes
  // (frontend never sees UPGRADING status).
  useEffect(() => {
    if (upgradingWorkers.size === 0) return;
    const timer = setTimeout(() => setUpgradingWorkers(new Map()), 30000);
    return () => clearTimeout(timer);
  }, [upgradingWorkers.size]);

  // Reconcile retiringWorkers: clear when server reports status !== RUNNING or worker disappears.
  // Only runs when server data changes (nodes). A 10s grace period prevents clearing
  // entries that were just added (server hasn't caught up yet).
  useEffect(() => {
    const GRACE_MS = 10_000;
    const now = Date.now();
    setRetiringWorkers((prev) => {
      if (prev.size === 0) return prev;
      const allWorkerKeys = new Set<string>();
      for (const node of nodes) {
        for (const ws of node.worker_summaries || []) {
          if (ws.instance_id) allWorkerKeys.add(`${node.id}:${ws.instance_id}`);
          if (ws.worker_id) allWorkerKeys.add(ws.worker_id);
        }
      }
      const toClear: string[] = [];
      for (const key of prev) {
        if (now - (retiringTimestamps.current.get(key) ?? 0) < GRACE_MS) continue;
        if (!allWorkerKeys.has(key)) {
          toClear.push(key);
          continue;
        }
        for (const node of nodes) {
          for (const ws of node.worker_summaries || []) {
            const wsKey = ws.instance_id ? `${node.id}:${ws.instance_id}` : ws.worker_id;
            if (wsKey === key && ws.status !== 'RUNNING') {
              toClear.push(key);
            }
          }
        }
      }
      if (toClear.length === 0) return prev;
      const next = new Set(prev);
      for (const key of toClear) {
        next.delete(key);
        retiringTimestamps.current.delete(key);
      }
      return next;
    });
  }, [nodes]);

  // Fallback timeout for retiringWorkers: 60s (graceful shutdown takes time)
  useEffect(() => {
    if (retiringWorkers.size === 0) return;
    const timer = setTimeout(() => {
      retiringTimestamps.current.clear();
      setRetiringWorkers(new Set());
    }, 60000);
    return () => clearTimeout(timer);
  }, [retiringWorkers.size]);

  // Reconcile cleaningUpWorkers, removingWorkers, deletingWorkers, removingNodes:
  // clear when items disappear from server data
  useEffect(() => {
    const currentNodeIds = new Set(nodes.map((n) => n.id));
    const currentWorkerKeys = new Set<string>();
    const currentWorkerIds = new Set<string>();
    for (const node of nodes) {
      for (const ws of node.worker_summaries || []) {
        if (ws.instance_id) currentWorkerKeys.add(`${node.id}:${ws.instance_id}`);
        if (ws.worker_id) currentWorkerIds.add(ws.worker_id);
      }
    }

    if (cleaningUpWorkers.size > 0) {
      const toClear = [...cleaningUpWorkers].filter((k) => !currentWorkerKeys.has(k));
      if (toClear.length > 0) {
        setCleaningUpWorkers((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
    if (removingWorkers.size > 0) {
      const toClear = [...removingWorkers].filter((k) => !currentWorkerKeys.has(k));
      if (toClear.length > 0) {
        setRemovingWorkers((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
    if (stoppingWorkers.size > 0) {
      const toClear: string[] = [];
      for (const key of stoppingWorkers) {
        if (!currentWorkerKeys.has(key)) {
          toClear.push(key);
          continue;
        }
        for (const node of nodes) {
          for (const ws of node.worker_summaries || []) {
            const wsKey = ws.instance_id ? `${node.id}:${ws.instance_id}` : null;
            if (wsKey === key && ws.status === 'STOPPED') {
              toClear.push(key);
            }
          }
        }
      }
      if (toClear.length > 0) {
        setStoppingWorkers((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
    if (deletingWorkers.size > 0) {
      const toClear = [...deletingWorkers].filter((k) => !currentWorkerIds.has(k));
      if (toClear.length > 0) {
        setDeletingWorkers((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
    if (removingNodes.size > 0) {
      const toClear = [...removingNodes].filter((k) => !currentNodeIds.has(k));
      if (toClear.length > 0) {
        setRemovingNodes((prev) => {
          const next = new Set(prev);
          for (const key of toClear) next.delete(key);
          return next;
        });
      }
    }
  }, [nodes, cleaningUpWorkers, removingWorkers, stoppingWorkers, deletingWorkers, removingNodes]);

  // Fallback timeout for cleaningUpWorkers, removingWorkers, deletingWorkers, removingNodes: 30s
  useEffect(() => {
    if (cleaningUpWorkers.size === 0) return;
    const timer = setTimeout(() => setCleaningUpWorkers(new Set()), 30000);
    return () => clearTimeout(timer);
  }, [cleaningUpWorkers.size]);
  useEffect(() => {
    if (removingWorkers.size === 0) return;
    const timer = setTimeout(() => setRemovingWorkers(new Set()), 30000);
    return () => clearTimeout(timer);
  }, [removingWorkers.size]);
  useEffect(() => {
    if (stoppingWorkers.size === 0) return;
    const timer = setTimeout(() => setStoppingWorkers(new Set()), 60000);
    return () => clearTimeout(timer);
  }, [stoppingWorkers.size]);
  useEffect(() => {
    if (deletingWorkers.size === 0) return;
    const timer = setTimeout(() => setDeletingWorkers(new Set()), 30000);
    return () => clearTimeout(timer);
  }, [deletingWorkers.size]);
  useEffect(() => {
    if (removingNodes.size === 0) return;
    const timer = setTimeout(() => setRemovingNodes(new Set()), 30000);
    return () => clearTimeout(timer);
  }, [removingNodes.size]);

  const toggleExpanded = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  return (
    <Fragment>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 48 }} />
              <TableCell>Name</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Node ID</TableCell>
              <TableCell>State</TableCell>
              <TableCell>OS</TableCell>
              <TableCell>CPU</TableCell>
              <TableCell>Memory</TableCell>
              <TableCell>Disk</TableCell>
              <TableCell>Running Workers</TableCell>
              <TableCell>Last Seen</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {nodes.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  No nodes registered. Start a node agent with: <code>tailor node-agent</code>
                </TableCell>
              </TableRow>
            )}
            {[...nodes]
              .sort((a, b) => a.id.localeCompare(b.id))
              .map((node) => {
                const workers =
                  node.state === 'OFFLINE'
                    ? []
                    : [...(node.worker_summaries || [])].sort((a, b) => {
                        // Managed workers first
                        const aManaged = a.managed !== false ? 1 : 0;
                        const bManaged = b.managed !== false ? 1 : 0;
                        if (aManaged !== bManaged) return bManaged - aManaged;
                        return (
                          (a.name || '').localeCompare(b.name || '') ||
                          (a.instance_id || '').localeCompare(b.instance_id || '')
                        );
                      });
                const hasWorkers = workers.length > 0;
                const isProvisioning = provisioningNodeId === node.id;
                const showWorkerSection = hasWorkers || isProvisioning;
                const isExpanded = expandedNodes.has(node.id);

                return (
                  <Fragment key={node.id}>
                    <TableRow>
                      <TableCell>
                        {showWorkerSection && (
                          <IconButton size="small" onClick={() => toggleExpanded(node.id)}>
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>{node.name || '-'}</TableCell>
                      <TableCell>{node.account_name || '-'}</TableCell>
                      <TableCell
                        css={css`
                          font-family: monospace;
                          font-size: 0.85rem;
                        `}
                      >
                        <Tooltip title={node.node_id}>
                          <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigator.clipboard.writeText(node.node_id).catch(() => {})}
                          >
                            {node.node_id.substring(0, 8)}...
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip label={node.state} color={node.state === 'ONLINE' ? 'success' : 'default'} size="small" />
                      </TableCell>
                      <TableCell>
                        {String(node.os_info?.platform ?? '-')} {String(node.os_info?.architecture ?? '')}
                      </TableCell>
                      <TableCell>
                        {node.state === 'ONLINE' && node.metrics?.cpu_percent != null
                          ? `${node.metrics.cpu_percent}% / ${node.resources?.cpu_count ?? '?'} cores`
                          : String(node.resources?.cpu_count ?? '-')}
                      </TableCell>
                      <TableCell>
                        {node.state === 'ONLINE' && node.metrics?.memory_percent != null
                          ? `${node.metrics.memory_percent}% / ${node.resources?.memory_gb ?? '?'} GB`
                          : node.resources?.memory_gb
                            ? `${node.resources.memory_gb} GB`
                            : '-'}
                      </TableCell>
                      <TableCell>
                        {node.state === 'ONLINE' && node.metrics?.disk_percent != null
                          ? `${node.metrics.disk_percent}% / ${node.metrics.disk_total_gb} GB`
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {node.state === 'OFFLINE'
                          ? '-'
                          : (() => {
                              const runningWorkers = workers.filter((w) => w.status === 'RUNNING');
                              const totalCores = runningWorkers.reduce((sum, w) => sum + (w.ncores || 0), 0);
                              const runningTasks = runningWorkers.reduce((sum, w) => sum + (w.running_tasks ?? 0), 0);
                              return `${runningWorkers.length} (${runningTasks} / ${totalCores} cores running)`;
                            })()}
                      </TableCell>
                      <TableCell>{formatLastSeen(node.last_seen)}</TableCell>
                      <TableCell>
                        {node.state === 'OFFLINE' ? (
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={() => {
                              setRemovingNodes((prev) => new Set(prev).add(node.id));
                              deleteNodeAgent(node.id).then(
                                () => onMutate(),
                                (error) => {
                                  console.error('Failed to remove node:', error);
                                  setRemovingNodes((prev) => {
                                    const next = new Set(prev);
                                    next.delete(node.id);
                                    return next;
                                  });
                                },
                              );
                            }}
                            disabled={removingNodes.has(node.id)}
                          >
                            {removingNodes.has(node.id) ? 'Removing\u2026' : 'Remove'}
                          </Button>
                        ) : (
                          <Button variant="outlined" size="small" onClick={() => onStartWorker(node.id)}>
                            Start Worker
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>

                    {showWorkerSection && (
                      <TableRow>
                        <TableCell colSpan={12} style={{ paddingBottom: 0, paddingTop: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                margin: 2,
                                padding: 2,
                                backgroundColor: 'grey.50',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'grey.200',
                              }}
                            >
                              <Typography variant="subtitle2" gutterBottom component="div">
                                Workers
                              </Typography>
                              {isProvisioning && (
                                <Alert
                                  severity="info"
                                  icon={<CircularProgress size={16} />}
                                  sx={{ mb: hasWorkers ? 2 : 0 }}
                                >
                                  Starting new worker...
                                </Alert>
                              )}
                              {hasWorkers && (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Name</TableCell>
                                      <TableCell>Workdir</TableCell>
                                      <TableCell>Worker ID</TableCell>
                                      <TableCell>PID</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell>Running Cores</TableCell>
                                      <TableCell>Package</TableCell>
                                      <TableCell>Python</TableCell>
                                      <TableCell>Projects</TableCell>
                                      <TableCell>Capabilities</TableCell>
                                      <TableCell>Last Seen</TableCell>
                                      <TableCell>Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {workers.map((worker) => {
                                      const errorMsg =
                                        worker.start_error || worker.cleanup_error || worker.upgrade_error;
                                      const isFailedStatus =
                                        worker.status === 'START_FAILED' ||
                                        worker.status === 'CLEANUP_FAILED' ||
                                        worker.status === 'UPGRADE_FAILED';
                                      const showInlineError = !!errorMsg && isFailedStatus;
                                      const errorLabel =
                                        worker.status === 'START_FAILED'
                                          ? 'Failed to start'
                                          : worker.status === 'UPGRADE_FAILED'
                                            ? 'Upgrade failed'
                                            : 'Cleanup failed';
                                      const rowKey = worker.instance_id || worker.worker_id || worker.name;
                                      return (
                                        <Fragment key={rowKey}>
                                          <TableRow>
                                            <TableCell>
                                              {worker.name}
                                              <Chip
                                                label={worker.managed !== false ? 'Managed' : 'Standalone'}
                                                size="small"
                                                variant="outlined"
                                                color={worker.managed !== false ? 'primary' : 'default'}
                                                sx={{ ml: 1, fontSize: '0.7rem' }}
                                              />
                                            </TableCell>
                                            <TableCell>{worker.workdir_name || '-'}</TableCell>
                                            <TableCell
                                              css={css`
                                                font-family: monospace;
                                                font-size: 0.85rem;
                                              `}
                                            >
                                              {worker.worker_id ? (
                                                <Tooltip title={worker.worker_id}>
                                                  <span
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() =>
                                                      navigator.clipboard.writeText(worker.worker_id!).catch(() => {})
                                                    }
                                                  >
                                                    {worker.worker_id.substring(0, 8)}
                                                    ...
                                                  </span>
                                                </Tooltip>
                                              ) : (
                                                '-'
                                              )}
                                            </TableCell>
                                            <TableCell>{worker.pid != null ? worker.pid : '-'}</TableCell>
                                            <TableCell>
                                              {(() => {
                                                const workerKey = worker.instance_id
                                                  ? `${node.id}:${worker.instance_id}`
                                                  : worker.worker_id;
                                                const displayStatus =
                                                  worker.status === 'RUNNING' &&
                                                  workerKey &&
                                                  retiringWorkers.has(workerKey)
                                                    ? 'RETIRING'
                                                    : worker.status === 'RUNNING' &&
                                                        workerKey &&
                                                        upgradingWorkers.has(workerKey)
                                                      ? 'UPGRADING'
                                                      : worker.status;
                                                return (
                                                  <Tooltip
                                                    title={errorMsg || ''}
                                                    disableHoverListener={!errorMsg || showInlineError}
                                                  >
                                                    <Chip
                                                      label={displayStatus}
                                                      color={
                                                        displayStatus === 'RUNNING'
                                                          ? 'success'
                                                          : displayStatus === 'STARTING' ||
                                                              displayStatus === 'UPGRADING'
                                                            ? 'info'
                                                            : displayStatus === 'CLEANUP_FAILED' ||
                                                                displayStatus === 'START_FAILED' ||
                                                                displayStatus === 'UPGRADE_FAILED'
                                                              ? 'error'
                                                              : displayStatus === 'RETIRING'
                                                                ? 'info'
                                                                : displayStatus === 'MISSING'
                                                                  ? 'warning'
                                                                  : 'default'
                                                      }
                                                      size="small"
                                                    />
                                                  </Tooltip>
                                                );
                                              })()}
                                            </TableCell>
                                            <TableCell>
                                              {worker.running_tasks ?? 0} / {worker.ncores}
                                            </TableCell>
                                            <TableCell>
                                              {worker.package_name || '-'}
                                              {worker.installed_version ? ` ${worker.installed_version}` : ''}
                                            </TableCell>
                                            <TableCell>{worker.venv_python_version || '-'}</TableCell>
                                            <TableCell>
                                              {worker.project_id_filter?.length ? (
                                                <Tooltip title={worker.project_id_filter.join(', ')}>
                                                  <Chip
                                                    label={`${worker.project_id_filter.length} project${worker.project_id_filter.length > 1 ? 's' : ''}`}
                                                    size="small"
                                                  />
                                                </Tooltip>
                                              ) : (
                                                'All'
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              {worker.capabilities?.length ? worker.capabilities.join(', ') : '-'}
                                            </TableCell>
                                            <TableCell>
                                              {worker.last_seen ? formatLastSeen(worker.last_seen) : '-'}
                                            </TableCell>
                                            <TableCell>
                                              {worker.managed !== false ? (
                                                <Fragment>
                                                  <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => {
                                                      if (worker.worker_id && worker.instance_id) {
                                                        setRetireTarget({
                                                          workerId: worker.worker_id,
                                                          workerName: worker.name,
                                                          packageName: worker.package_name,
                                                          packageVersion:
                                                            worker.installed_version || worker.package_version,
                                                          trackingKey: `${node.id}:${worker.instance_id}`,
                                                        });
                                                      }
                                                    }}
                                                    disabled={
                                                      worker.status !== 'RUNNING' ||
                                                      !worker.worker_id ||
                                                      !worker.instance_id ||
                                                      retiringWorkers.has(`${node.id}:${worker.instance_id}`) ||
                                                      upgradingWorkers.has(`${node.id}:${worker.instance_id}`)
                                                    }
                                                    sx={{ mr: 1 }}
                                                  >
                                                    {retiringWorkers.has(`${node.id}:${worker.instance_id}`) &&
                                                    worker.status === 'RUNNING'
                                                      ? 'Retiring\u2026'
                                                      : 'Retire'}
                                                  </Button>
                                                  <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                      if (worker.instance_id) {
                                                        setStopTarget({
                                                          nodeAgentId: node.id,
                                                          instanceId: worker.instance_id,
                                                          workerName: worker.name,
                                                        });
                                                      }
                                                    }}
                                                    disabled={
                                                      worker.status !== 'RETIRING' ||
                                                      !worker.instance_id ||
                                                      stoppingWorkers.has(`${node.id}:${worker.instance_id}`)
                                                    }
                                                    sx={{ mr: 1 }}
                                                  >
                                                    {stoppingWorkers.has(`${node.id}:${worker.instance_id}`)
                                                      ? 'Stopping\u2026'
                                                      : 'Stop'}
                                                  </Button>
                                                  <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => {
                                                      if (worker.instance_id && worker.package_name) {
                                                        setUpgradeTarget({
                                                          nodeAgentId: node.id,
                                                          instanceId: worker.instance_id,
                                                          workerName: worker.name,
                                                          currentPackageName: worker.package_name,
                                                          currentVersion:
                                                            worker.installed_version || worker.package_version,
                                                          workdirName: worker.workdir_name,
                                                        });
                                                      }
                                                    }}
                                                    disabled={
                                                      worker.status !== 'RUNNING' ||
                                                      !worker.instance_id ||
                                                      upgradingWorkers.has(`${node.id}:${worker.instance_id}`) ||
                                                      retiringWorkers.has(`${node.id}:${worker.instance_id}`)
                                                    }
                                                    sx={{ mr: 1 }}
                                                  >
                                                    {worker.status === 'UPGRADING' ||
                                                    upgradingWorkers.has(`${node.id}:${worker.instance_id}`)
                                                      ? 'Upgrading\u2026'
                                                      : 'Upgrade'}
                                                  </Button>
                                                  {worker.status === 'CLEANUP_FAILED' ||
                                                  worker.status === 'START_FAILED' ||
                                                  worker.status === 'UPGRADE_FAILED' ||
                                                  worker.status === 'MISSING' ? (
                                                    <Button
                                                      variant="outlined"
                                                      size="small"
                                                      color="error"
                                                      disabled={
                                                        !worker.instance_id ||
                                                        removingWorkers.has(`${node.id}:${worker.instance_id}`)
                                                      }
                                                      onClick={() => {
                                                        if (!worker.instance_id) return;
                                                        const key = `${node.id}:${worker.instance_id}`;
                                                        setRemovingWorkers((prev) => new Set(prev).add(key));
                                                        sendNodeCommand(node.id, 'remove_worker', {
                                                          instance_id: worker.instance_id,
                                                        }).then(
                                                          () => onMutate(),
                                                          (error) => {
                                                            console.error('Failed to remove worker:', error);
                                                            setRemovingWorkers((prev) => {
                                                              const next = new Set(prev);
                                                              next.delete(key);
                                                              return next;
                                                            });
                                                          },
                                                        );
                                                      }}
                                                    >
                                                      {removingWorkers.has(`${node.id}:${worker.instance_id}`)
                                                        ? 'Removing\u2026'
                                                        : 'Remove'}
                                                    </Button>
                                                  ) : (
                                                    <Button
                                                      variant="outlined"
                                                      size="small"
                                                      color="error"
                                                      onClick={() =>
                                                        worker.instance_id &&
                                                        setCleanupTarget({
                                                          nodeAgentId: node.id,
                                                          instanceId: worker.instance_id,
                                                          workerName: worker.name,
                                                        })
                                                      }
                                                      disabled={
                                                        worker.status !== 'STOPPED' ||
                                                        !worker.instance_id ||
                                                        cleaningUpWorkers.has(`${node.id}:${worker.instance_id}`)
                                                      }
                                                    >
                                                      {cleaningUpWorkers.has(`${node.id}:${worker.instance_id}`)
                                                        ? 'Cleaning up\u2026'
                                                        : 'Clean Up'}
                                                    </Button>
                                                  )}
                                                </Fragment>
                                              ) : (
                                                <Fragment>
                                                  {worker.status === 'RUNNING' && worker.worker_id && (
                                                    <Button
                                                      variant="outlined"
                                                      size="small"
                                                      onClick={() => {
                                                        setRetireTarget({
                                                          workerId: worker.worker_id!,
                                                          workerName: worker.name,
                                                          packageName: worker.package_name,
                                                          packageVersion:
                                                            worker.installed_version || worker.package_version,
                                                          trackingKey: worker.worker_id!,
                                                        });
                                                      }}
                                                      disabled={retiringWorkers.has(worker.worker_id)}
                                                      sx={{ mr: 1 }}
                                                    >
                                                      {retiringWorkers.has(worker.worker_id)
                                                        ? 'Retiring\u2026'
                                                        : 'Retire'}
                                                    </Button>
                                                  )}
                                                  {worker.status === 'MISSING' && worker.worker_id && (
                                                    <Button
                                                      variant="outlined"
                                                      size="small"
                                                      color="error"
                                                      onClick={() => {
                                                        const wid = worker.worker_id!;
                                                        setDeletingWorkers((prev) => new Set(prev).add(wid));
                                                        deleteWorker(wid).then(
                                                          () => onMutate(),
                                                          (error) => {
                                                            console.error('Failed to delete worker:', error);
                                                            setDeletingWorkers((prev) => {
                                                              const next = new Set(prev);
                                                              next.delete(wid);
                                                              return next;
                                                            });
                                                          },
                                                        );
                                                      }}
                                                      disabled={deletingWorkers.has(worker.worker_id)}
                                                    >
                                                      {deletingWorkers.has(worker.worker_id)
                                                        ? 'Removing\u2026'
                                                        : 'Remove'}
                                                    </Button>
                                                  )}
                                                </Fragment>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                          {showInlineError && (
                                            <TableRow>
                                              <TableCell
                                                colSpan={12}
                                                sx={{ py: 0.5, borderBottom: 0, backgroundColor: 'transparent' }}
                                              >
                                                <Alert
                                                  severity="error"
                                                  variant="outlined"
                                                  sx={{ mb: 1, alignItems: 'flex-start' }}
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    component="div"
                                                    sx={{ fontWeight: 600, mb: 0.5 }}
                                                  >
                                                    {errorLabel}
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    component="pre"
                                                    sx={{
                                                      m: 0,
                                                      whiteSpace: 'pre-wrap',
                                                      wordBreak: 'break-word',
                                                      fontFamily:
                                                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                                      fontSize: '0.8rem',
                                                    }}
                                                  >
                                                    {errorMsg}
                                                  </Typography>
                                                </Alert>
                                              </TableCell>
                                            </TableRow>
                                          )}
                                        </Fragment>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {cleanupTarget && (
        <CleanupWorkerDialog
          open={true}
          nodeAgentId={cleanupTarget.nodeAgentId}
          instanceId={cleanupTarget.instanceId}
          workerName={cleanupTarget.workerName}
          onClose={() => setCleanupTarget(null)}
          onConfirm={() => {
            setCleaningUpWorkers((prev) =>
              new Set(prev).add(`${cleanupTarget.nodeAgentId}:${cleanupTarget.instanceId}`),
            );
            onMutate();
          }}
        />
      )}
      {stopTarget && (
        <StopWorkerDialog
          open={true}
          nodeAgentId={stopTarget.nodeAgentId}
          instanceId={stopTarget.instanceId}
          workerName={stopTarget.workerName}
          onClose={() => setStopTarget(null)}
          onConfirm={() => {
            setStoppingWorkers((prev) => new Set(prev).add(`${stopTarget.nodeAgentId}:${stopTarget.instanceId}`));
            onMutate();
          }}
        />
      )}
      {retireTarget && (
        <RetireWorkerDialog
          open={true}
          workerId={retireTarget.workerId}
          workerName={retireTarget.workerName}
          packageName={retireTarget.packageName}
          packageVersion={retireTarget.packageVersion}
          onClose={() => setRetireTarget(null)}
          onConfirm={() => {
            retiringTimestamps.current.set(retireTarget.trackingKey, Date.now());
            setRetiringWorkers((prev) => new Set(prev).add(retireTarget.trackingKey));
            onMutate();
          }}
        />
      )}
      {upgradeTarget && (
        <UpgradeWorkerDialog
          open={true}
          nodeAgentId={upgradeTarget.nodeAgentId}
          instanceId={upgradeTarget.instanceId}
          workerName={upgradeTarget.workerName}
          currentPackageName={upgradeTarget.currentPackageName}
          currentVersion={upgradeTarget.currentVersion}
          onClose={() => setUpgradeTarget(null)}
          onConfirm={() => {
            const nodeId = upgradeTarget.nodeAgentId;
            const node = nodes.find((n) => n.id === nodeId);
            const allWorkers = node?.worker_summaries || [];
            const affected = upgradeTarget.workdirName
              ? allWorkers.filter((ws) => ws.workdir_name === upgradeTarget.workdirName && ws.instance_id)
              : allWorkers.filter((ws) => ws.instance_id === upgradeTarget.instanceId);
            setUpgradingWorkers((prev) => {
              const next = new Map(prev);
              for (const ws of affected) {
                const key = `${nodeId}:${ws.instance_id}`;
                next.set(key, ws.installed_version || ws.package_version);
              }
              return next;
            });
            onMutate();
          }}
        />
      )}
    </Fragment>
  );
}
